import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    Box,
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Drawer,
    FormControlLabel,
    Grid,
    IconButton,
    InputAdornment,
    Radio,
    RadioGroup,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    useTheme,
    Theme,
} from '@material-ui/core'
import { Add } from '@material-ui/icons'
import SearchIcon from '@material-ui/icons/Search'
import MailIcon from '@material-ui/icons/Mail'
import EmailIcon from '@material-ui/icons/Email'
import SmsIcon from '@material-ui/icons/Sms'
import InboxIcon from '@material-ui/icons/Inbox'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import VisibilityIcon from '@material-ui/icons/Visibility'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import FilterListIcon from '@material-ui/icons/FilterList'
import CloseIcon from '@material-ui/icons/Close'
import FolderIcon from '@material-ui/icons/Folder'
import HomeIcon from '@material-ui/icons/Home'
import PersonIcon from '@material-ui/icons/Person'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import ArchiveIcon from '@material-ui/icons/Archive'
import UnarchiveIcon from '@material-ui/icons/Unarchive'

import { Container, Selector } from '../../components'
import { TenantMessage } from '../../models/TenantMessage'
import { Area } from '../../models'
import { SEND_TENANT_MESSAGES } from '../../models/Permissions'
import { hasPermission } from '../../models/Users/services'
import { useUser, useRootInfrastructure, useAptConfig } from '../../hooks'
import {
    useTenantMessages,
    MessageFilters,
    MessageOrdering,
} from '../../hooks/useTenantMessages'
import { ComposeMessageModal } from './ComposeMessageModal'
import { RecipientsModal } from './RecipientsModal'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        height: 'calc(100vh - 140px)',
    },
    kpi: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: '#fff',
    },
    kpiLabel: {
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: theme.palette.text.secondary,
    },
    kpiValue: {
        marginTop: theme.spacing(0.5),
        fontSize: '1.5rem',
        fontWeight: 700,
        fontFamily: 'monospace',
    },
    tableCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    tableHead: {
        backgroundColor: theme.palette.grey[50],
        '& .MuiTableCell-head': {
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
        },
    },
    filterRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
    },
    search: {
        flex: '1 1 240px',
        minWidth: 200,
    },
    readBar: {
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.palette.grey[200],
    },
    readBarFill: {
        height: '100%',
        borderRadius: 3,
        backgroundColor: '#10b981',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        textAlign: 'center',
        padding: theme.spacing(6),
    },
    emptyIcon: {
        width: 56,
        height: 56,
        borderRadius: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,140,133,0.08)',
        color: '#008C85',
        marginBottom: theme.spacing(2),
    },
    drawer: {
        width: 360,
        padding: theme.spacing(2.5),
    },
    drawerSection: {
        marginBottom: theme.spacing(2.5),
        padding: theme.spacing(1.5, 2),
        borderRadius: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#fafbfc',
        '& .MuiFormControlLabel-root': {
            marginLeft: -4,
            marginRight: 0,
            '& .MuiTypography-root': {
                fontSize: '0.8125rem',
            },
        },
        '& .MuiRadio-root': {
            padding: 6,
        },
        '& .MuiRadioGroup-root': {
            marginTop: theme.spacing(0.75),
        },
    },
    drawerSectionLabel: {
        fontSize: '0.6875rem',
        fontWeight: 700,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.06em',
        color: theme.palette.text.secondary,
        display: 'block',
    },
    activeChips: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
    },
    drawerTabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        marginBottom: theme.spacing(2),
        minHeight: 40,
        '& .MuiTab-root': {
            minHeight: 40,
            minWidth: 'auto',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8125rem',
            padding: theme.spacing(0, 2),
        },
        '& .MuiTabs-indicator': {
            height: 3,
            borderRadius: '3px 3px 0 0',
        },
    },
    treeContainer: {
        flex: 1,
        overflowY: 'auto',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: '#fafbfc',
    },
    treeSearch: {
        marginBottom: theme.spacing(1.5),
        '& .MuiOutlinedInput-root': {
            borderRadius: theme.spacing(1),
            backgroundColor: '#fff',
        },
    },
    treeNode: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        textAlign: 'left',
        padding: theme.spacing(0.75, 1),
        borderRadius: theme.spacing(0.75),
        transition: 'all 0.15s ease',
        border: '1px solid transparent',
        marginBottom: 2,
        '&:hover': {
            backgroundColor: '#f0f4f8',
            borderColor: theme.palette.divider,
        },
    },
    treeNodeSelected: {
        backgroundColor: 'rgba(0,140,133,0.06)',
        borderColor: 'rgba(0,140,133,0.3)',
        '&:hover': {
            backgroundColor: 'rgba(0,140,133,0.1)',
            borderColor: 'rgba(0,140,133,0.4)',
        },
    },
    treeNodeIcon: {
        width: 22,
        height: 22,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: theme.spacing(0.5),
        flexShrink: 0,
    },
    treeNodeLabel: {
        fontSize: '0.8125rem',
        fontWeight: 500,
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        color: theme.palette.text.primary,
    },
    treeNodeLabelSelected: {
        color: '#008C85',
        fontWeight: 600,
    },
}))

const formatDate = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    })
}

const formatTime = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    })
}

type SortableColumn =
    | 'subject'
    | 'recipient_count'
    | 'last_activity'
    | 'created_date'
    | 'latest_reply_at'

const COLUMN_ORDERING_MAP: Record<
    SortableColumn,
    { asc: MessageOrdering; desc: MessageOrdering }
> = {
    subject: { asc: 'subject', desc: '-subject' },
    recipient_count: { asc: 'recipient_count', desc: '-recipient_count' },
    last_activity: { asc: 'last_activity', desc: '-last_activity' },
    created_date: { asc: 'created_date', desc: '-created_date' },
    latest_reply_at: { asc: 'latest_reply_at', desc: '-latest_reply_at' },
}

interface LocationTreeProps {
    tree: any
    occupiableAreaConfigIds: Set<number>
    selectedUnitIds: number[]
    selectedAreaIds: number[]
    searchQuery: string
    openState: Record<string, boolean>
    setOpenState: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
    onSelectUnit: (unitId: number) => void
    onSelectArea: (areaId: number) => void
    onSelectFolder: (unitIds: number[]) => void
}

const LocationTree: React.FC<LocationTreeProps> = ({
    tree,
    occupiableAreaConfigIds,
    selectedUnitIds,
    selectedAreaIds,
    searchQuery,
    openState,
    setOpenState,
    onSelectUnit,
    onSelectArea,
    onSelectFolder,
}) => {
    const classes = useStyles()
    const theme = useTheme()

    if (!tree) {
        return (
            <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16, textAlign: 'center' }}
            >
                Loading locations...
            </Typography>
        )
    }

    const query = searchQuery.toLowerCase()

    const hasOccupiableAreas = (unit: any): boolean => {
        return unit.areas?.some((area: Area) =>
            occupiableAreaConfigIds.has(area.area_config),
        )
    }

    const getOccupiableAreas = (unit: any): Area[] => {
        return (unit.areas || []).filter((area: Area) =>
            occupiableAreaConfigIds.has(area.area_config),
        )
    }

    const collectFolderUnitIds = (folder: any): number[] => {
        const ids: number[] = []
        ;(folder.units || []).forEach((unit: any) => {
            if (hasOccupiableAreas(unit)) ids.push(unit.id)
        })
        ;(folder.children || []).forEach((child: any) => {
            ids.push(...collectFolderUnitIds(child))
        })
        return ids
    }

    const matchesSearch = (unit: any): boolean => {
        if (!query) return true
        if (unit.name?.toLowerCase().includes(query)) return true
        return getOccupiableAreas(unit).some((area: Area) => {
            const label = `${area.area_config_name || ''} ${area.area_label}`
                .trim()
                .toLowerCase()
            return label.includes(query)
        })
    }

    const folderHasMatchingUnits = (folder: any): boolean => {
        const unitsMatch = (folder.units || []).some(
            (unit: any) => hasOccupiableAreas(unit) && matchesSearch(unit),
        )
        if (unitsMatch) return true
        return (folder.children || []).some((child: any) =>
            folderHasMatchingUnits(child),
        )
    }

    const renderFolder = (folder: any, level: number): JSX.Element[] => {
        const elements: JSX.Element[] = []

        if (!folderHasMatchingUnits(folder)) return elements

        if (folder.name) {
            const isOpen = openState[`folder-${folder.id}`] !== false
            const folderUnitIds = collectFolderUnitIds(folder)
            const allSelected =
                folderUnitIds.length > 0 &&
                folderUnitIds.every((id) => selectedUnitIds.includes(id))
            const someSelected =
                !allSelected &&
                folderUnitIds.some((id) => selectedUnitIds.includes(id))

            elements.push(
                <Box
                    key={`folder-${folder.id}`}
                    display="flex"
                    alignItems="center"
                    style={{ paddingLeft: 8 + level * 16, marginBottom: 2 }}
                >
                    <ButtonBase
                        className={classes.treeNode}
                        style={{ flex: 1, paddingLeft: 0 }}
                        onClick={() =>
                            setOpenState((prev) => ({
                                ...prev,
                                [`folder-${folder.id}`]: !isOpen,
                            }))
                        }
                    >
                        <Box className={classes.treeNodeIcon}>
                            {isOpen ? (
                                <ExpandMoreIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            ) : (
                                <ChevronRightIcon
                                    style={{
                                        fontSize: 16,
                                        color: theme.palette.text.secondary,
                                    }}
                                />
                            )}
                        </Box>
                        <Box
                            className={classes.treeNodeIcon}
                            style={{ marginRight: 4 }}
                        >
                            <FolderIcon
                                style={{
                                    fontSize: 16,
                                    color: theme.palette.warning.main,
                                }}
                            />
                        </Box>
                        <span
                            className={classes.treeNodeLabel}
                            style={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.03em',
                                color:
                                    someSelected || allSelected
                                        ? '#008C85'
                                        : theme.palette.text.secondary,
                            }}
                        >
                            {folder.name}
                        </span>
                    </ButtonBase>
                    <Tooltip
                        title={
                            allSelected
                                ? 'Deselect all units in folder'
                                : 'Select all units in folder'
                        }
                    >
                        <ButtonBase
                            onClick={(e) => {
                                e.stopPropagation()
                                onSelectFolder(folderUnitIds)
                            }}
                            style={{
                                fontSize: '0.5625rem',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: 4,
                                backgroundColor: allSelected
                                    ? '#008C85'
                                    : someSelected
                                    ? 'rgba(0,140,133,0.1)'
                                    : theme.palette.grey[100],
                                color: allSelected
                                    ? '#fff'
                                    : someSelected
                                    ? '#008C85'
                                    : theme.palette.text.secondary,
                                transition: 'all 0.15s',
                                marginLeft: 4,
                            }}
                        >
                            {allSelected ? 'All' : `${folderUnitIds.length}`}
                        </ButtonBase>
                    </Tooltip>
                </Box>,
            )

            if (!isOpen) return elements
        }

        const childLevel = folder.name ? level + 1 : level

        ;(folder.children || []).forEach((child: any) => {
            elements.push(...renderFolder(child, childLevel))
        })
        ;(folder.units || []).forEach((unit: any) => {
            if (!hasOccupiableAreas(unit) || !matchesSearch(unit)) return

            const occupiableAreas = getOccupiableAreas(unit)
            const isUnitSelected = selectedUnitIds.includes(unit.id)
            const unitOpen = openState[`unit-${unit.id}`] !== false

            elements.push(
                <Box
                    key={`unit-${unit.id}`}
                    display="flex"
                    alignItems="center"
                    style={{ paddingLeft: 8 + childLevel * 16 }}
                >
                    <IconButton
                        size="small"
                        style={{ padding: 2, marginRight: 2 }}
                        onClick={() =>
                            setOpenState((prev) => ({
                                ...prev,
                                [`unit-${unit.id}`]: !unitOpen,
                            }))
                        }
                    >
                        {unitOpen ? (
                            <ExpandMoreIcon style={{ fontSize: 14 }} />
                        ) : (
                            <ChevronRightIcon style={{ fontSize: 14 }} />
                        )}
                    </IconButton>
                    <ButtonBase
                        className={`${classes.treeNode} ${
                            isUnitSelected ? classes.treeNodeSelected : ''
                        }`}
                        style={{ paddingLeft: 4, flex: 1 }}
                        onClick={() => onSelectUnit(unit.id)}
                    >
                        <Box
                            className={classes.treeNodeIcon}
                            style={{ marginRight: 4 }}
                        >
                            <HomeIcon
                                style={{
                                    fontSize: 16,
                                    color: isUnitSelected
                                        ? '#008C85'
                                        : theme.palette.text.secondary,
                                }}
                            />
                        </Box>
                        <span
                            className={`${classes.treeNodeLabel} ${
                                isUnitSelected
                                    ? classes.treeNodeLabelSelected
                                    : ''
                            }`}
                        >
                            {unit.name}
                        </span>
                        <Typography
                            variant="caption"
                            style={{
                                color: theme.palette.text.disabled,
                                fontSize: '0.625rem',
                                marginLeft: 4,
                            }}
                        >
                            {occupiableAreas.length}
                        </Typography>
                    </ButtonBase>
                </Box>,
            )

            if (unitOpen) {
                occupiableAreas.forEach((area: Area) => {
                    const areaLabel = `${area.area_config_name || ''} ${
                        area.area_label
                    }`.trim()
                    const isAreaSelected = selectedAreaIds.includes(area.id)

                    elements.push(
                        <ButtonBase
                            key={`area-${area.id}`}
                            className={`${classes.treeNode} ${
                                isAreaSelected ? classes.treeNodeSelected : ''
                            }`}
                            style={{ paddingLeft: 8 + (childLevel + 1) * 16 }}
                            onClick={() => onSelectArea(area.id)}
                        >
                            <Box className={classes.treeNodeIcon}>
                                <PersonIcon
                                    style={{
                                        fontSize: 14,
                                        color: isAreaSelected
                                            ? '#008C85'
                                            : theme.palette.text.secondary,
                                    }}
                                />
                            </Box>
                            <span
                                className={`${classes.treeNodeLabel} ${
                                    isAreaSelected
                                        ? classes.treeNodeLabelSelected
                                        : ''
                                }`}
                                style={{ fontSize: '0.75rem' }}
                            >
                                {areaLabel}
                            </span>
                        </ButtonBase>,
                    )
                })
            }
        })

        return elements
    }

    const elements = renderFolder(tree, 0)

    if (elements.length === 0) {
        return (
            <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: 16, textAlign: 'center' }}
            >
                {query
                    ? 'No matching units or areas'
                    : 'No occupiable areas found'}
            </Typography>
        )
    }

    return <>{elements}</>
}

export const TenantMessagesHome: React.FC = () => {
    const theme = useTheme()
    const classes = useStyles()
    const { workspaceUser, userList, actions: userActions } = useUser()

    const canSendMessages = hasPermission(workspaceUser, SEND_TENANT_MESSAGES)

    useEffect(() => {
        userActions.getUserList({ params: { my_team: true } })
    }, [workspaceUser?.active_workspace?.id])

    const { tree, unitList } = useRootInfrastructure(true)
    const { areaConfigList } = useAptConfig({ areaConfigList: true })

    const occupiableAreaConfigIds = useMemo(() => {
        const ids = new Set<number>()
        areaConfigList?.forEach((config) => {
            if (config.occupiable) ids.add(config.id)
        })
        return ids
    }, [areaConfigList])

    const unitNameMap = useMemo(() => {
        const map: Record<number, string> = {}
        unitList?.forEach((u) => {
            map[u.id] = u.name
        })
        return map
    }, [unitList])

    const areaNameMap = useMemo(() => {
        const map: Record<number, string> = {}
        unitList?.forEach((unit) => {
            unit.areas.forEach((area) => {
                if (occupiableAreaConfigIds.has(area.area_config)) {
                    map[area.id] = `${unit.name} - ${
                        area.area_config_name || ''
                    } ${area.area_label}`.trim()
                }
            })
        })
        return map
    }, [unitList, occupiableAreaConfigIds])

    const [composeOpen, setComposeOpen] = useState(false)
    const [
        selectedMessage,
        setSelectedMessage,
    ] = useState<TenantMessage | null>(null)
    const [query, setQuery] = useState('')
    const [filters, setFilters] = useState<MessageFilters>({
        date_range: 'last_30_days',
    })
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [drawerTab, setDrawerTab] = useState(0)
    const [treeSearch, setTreeSearch] = useState('')
    const [treeOpenState, setTreeOpenState] = useState<Record<string, boolean>>(
        {},
    )
    const [selectedIds, setSelectedIds] = useState<number[]>([])
    const [viewMode, setViewMode] = useState<'active' | 'archived'>('active')
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    const effectiveFilters: MessageFilters = useMemo(() => {
        const f = { ...filters }
        if (viewMode === 'archived') {
            f.archived = 'true'
        }
        return f
    }, [filters, viewMode])

    const {
        messages,
        totalCount,
        hasNextPage,
        hasPreviousPage,
        isLoading,
        isApartment,
        archiveMessage,
        unarchiveMessage,
        bulkArchive,
        bulkUnarchive,
    } = useTenantMessages(effectiveFilters)
    const currentPage = filters.page || 1
    const pageSize = filters.page_size || 25
    const totalPages = Math.ceil(totalCount / pageSize)

    useEffect(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        searchTimerRef.current = setTimeout(() => {
            setFilters((f) => ({
                ...f,
                search: query.trim() || undefined,
                page: 1,
            }))
        }, 400)
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
        }
    }, [query])

    const stats = useMemo(() => {
        const totalSent = totalCount
        const totalRecipients = messages.reduce(
            (sum, m) => sum + m.recipient_count,
            0,
        )
        const totalRead = messages.reduce((sum, m) => sum + m.read_count, 0)
        const readRate =
            totalRecipients > 0
                ? Math.round((totalRead / totalRecipients) * 100)
                : 0
        return { totalSent, totalRecipients, totalRead, readRate }
    }, [messages, totalCount])

    const activeFilterCount = useMemo(() => {
        let count = 0
        if (filters.initiated_by) count++
        if (filters.has_replies !== undefined) count++
        if (filters.unread_replies) count++
        if (filters.audience_type) count++
        if (filters.individual) count++
        if (filters.channel) count++
        if (filters.read_status) count++
        if (filters.unit_ids && filters.unit_ids.length > 0) count++
        if (filters.area_ids && filters.area_ids.length > 0) count++
        if (filters.created_by) count++
        if (filters.date_range) count++
        if (filters.created_after) count++
        if (filters.created_before) count++
        return count
    }, [filters])

    const currentSort: {
        column: SortableColumn
        direction: 'asc' | 'desc'
    } = useMemo(() => {
        const ordering = filters.ordering || '-last_activity'
        for (const [col, map] of Object.entries(COLUMN_ORDERING_MAP)) {
            if (ordering === map.asc)
                return { column: col as SortableColumn, direction: 'asc' }
            if (ordering === map.desc)
                return { column: col as SortableColumn, direction: 'desc' }
        }
        return { column: 'last_activity', direction: 'desc' }
    }, [filters.ordering])

    const handleSort = (column: SortableColumn) => {
        const map = COLUMN_ORDERING_MAP[column]
        if (currentSort.column === column) {
            setFilters((f) => ({
                ...f,
                ordering: currentSort.direction === 'desc' ? map.asc : map.desc,
                page: 1,
            }))
        } else {
            setFilters((f) => ({ ...f, ordering: map.desc, page: 1 }))
        }
    }

    const removeFilter = (key: keyof MessageFilters) => {
        setFilters((f) => {
            const next = { ...f, page: 1 }
            delete next[key]
            return next
        })
    }

    const clearAllFilters = () => {
        setFilters((f) => ({ ordering: f.ordering, page: 1 }))
    }

    const [archiveConfirm, setArchiveConfirm] = useState<{
        type: 'single' | 'bulk'
        ids: number[]
    } | null>(null)

    const handleArchive = (msgId: number) => {
        setArchiveConfirm({ type: 'single', ids: [msgId] })
    }

    const handleUnarchive = (msgId: number) => {
        unarchiveMessage.mutate(msgId)
        setSelectedIds((prev) => prev.filter((id) => id !== msgId))
    }

    const handleBulkArchive = () => {
        if (selectedIds.length === 0) return
        setArchiveConfirm({ type: 'bulk', ids: [...selectedIds] })
    }

    const confirmArchive = () => {
        if (!archiveConfirm) return
        if (archiveConfirm.type === 'bulk') {
            bulkArchive.mutate(archiveConfirm.ids)
            setSelectedIds([])
        } else {
            archiveMessage.mutate(archiveConfirm.ids[0])
            setSelectedIds((prev) =>
                prev.filter((id) => id !== archiveConfirm.ids[0]),
            )
        }
        setArchiveConfirm(null)
    }

    const toggleSelectAll = () => {
        if (selectedIds.length === messages.length) {
            setSelectedIds([])
        } else {
            setSelectedIds(messages.map((m) => m.id))
        }
    }

    const toggleSelect = (id: number) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
        )
    }

    useEffect(() => {
        setSelectedIds([])
    }, [viewMode])

    if (!isApartment) {
        return (
            <Container
                direction="column"
                alignItems="center"
                justifyContent="center"
                style={{ height: 'calc(100vh - 140px)', textAlign: 'center' }}
            >
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                    Tenant Messages are managed per apartment
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: theme.spacing(1), maxWidth: 420 }}
                >
                    Select an apartment workspace from the top bar to send and
                    view tenant messages.
                </Typography>
            </Container>
        )
    }

    return (
        <>
            <Box className={classes.root}>
                {/* Header */}
                <Box display="flex" alignItems="center">
                    <Box flex={1}>
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                            Tenant Messages
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Messages and conversations with tenants at this
                            property.
                        </Typography>
                    </Box>
                    {canSendMessages && (
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Add />}
                            onClick={() => setComposeOpen(true)}
                            style={{ textTransform: 'none', fontWeight: 600 }}
                        >
                            New Message
                        </Button>
                    )}
                </Box>

                {isLoading ? (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flex={1}
                    >
                        <CircularProgress />
                    </Box>
                ) : messages.length === 0 &&
                  activeFilterCount === 0 &&
                  viewMode === 'active' ? (
                    <Box className={classes.emptyState}>
                        <Box className={classes.emptyIcon}>
                            <MailIcon style={{ fontSize: 28 }} />
                        </Box>
                        <Typography
                            variant="h6"
                            style={{ fontWeight: 600, marginBottom: 4 }}
                        >
                            No messages sent yet
                        </Typography>
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ maxWidth: 360 }}
                        >
                            Send your first message to tenants. You can message
                            everyone at once or select specific tenants.
                        </Typography>
                        {canSendMessages && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<Add />}
                                onClick={() => setComposeOpen(true)}
                                style={{ textTransform: 'none', marginTop: 16 }}
                            >
                                Compose Message
                            </Button>
                        )}
                    </Box>
                ) : (
                    <>
                        {/* Active / Archived Tabs */}
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{
                                gap: 4,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                paddingBottom: 0,
                            }}
                        >
                            <ButtonBase
                                onClick={() => {
                                    setViewMode('active')
                                    setFilters((f) => ({ ...f, page: 1 }))
                                }}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '0.8125rem',
                                    fontWeight:
                                        viewMode === 'active' ? 700 : 500,
                                    color:
                                        viewMode === 'active'
                                            ? '#008C85'
                                            : theme.palette.text.secondary,
                                    borderBottom:
                                        viewMode === 'active'
                                            ? '2px solid #008C85'
                                            : '2px solid transparent',
                                    borderRadius: 0,
                                    marginBottom: -1,
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <InboxIcon
                                    style={{ fontSize: 16, marginRight: 6 }}
                                />
                                Active
                            </ButtonBase>
                            <ButtonBase
                                onClick={() => {
                                    setViewMode('archived')
                                    setFilters((f) => ({ ...f, page: 1 }))
                                }}
                                style={{
                                    padding: '8px 16px',
                                    fontSize: '0.8125rem',
                                    fontWeight:
                                        viewMode === 'archived' ? 700 : 500,
                                    color:
                                        viewMode === 'archived'
                                            ? '#008C85'
                                            : theme.palette.text.secondary,
                                    borderBottom:
                                        viewMode === 'archived'
                                            ? '2px solid #008C85'
                                            : '2px solid transparent',
                                    borderRadius: 0,
                                    marginBottom: -1,
                                    transition: 'all 0.15s ease',
                                }}
                            >
                                <ArchiveIcon
                                    style={{ fontSize: 16, marginRight: 6 }}
                                />
                                Archived
                            </ButtonBase>
                        </Box>

                        {/* Bulk action bar */}
                        <Collapse in={selectedIds.length > 0}>
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{
                                    gap: 12,
                                    padding: '10px 16px',
                                    borderRadius: 8,
                                    backgroundColor: 'rgba(0,140,133,0.06)',
                                    border: '1px solid rgba(0,140,133,0.2)',
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    style={{
                                        fontWeight: 600,
                                        color: '#008C85',
                                    }}
                                >
                                    {selectedIds.length} selected
                                </Typography>
                                {viewMode === 'active' ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            <ArchiveIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        }
                                        onClick={handleBulkArchive}
                                        disabled={bulkArchive.isLoading}
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            borderColor: '#008C85',
                                            color: '#008C85',
                                        }}
                                    >
                                        Archive Selected
                                    </Button>
                                ) : (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={
                                            <UnarchiveIcon
                                                style={{ fontSize: 16 }}
                                            />
                                        }
                                        onClick={() => {
                                            bulkUnarchive.mutate(selectedIds)
                                            setSelectedIds([])
                                        }}
                                        disabled={bulkUnarchive.isLoading}
                                        style={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                            borderColor: '#008C85',
                                            color: '#008C85',
                                        }}
                                    >
                                        Unarchive Selected
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    onClick={() => setSelectedIds([])}
                                    style={{
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        marginLeft: 'auto',
                                    }}
                                >
                                    Deselect
                                </Button>
                            </Box>
                        </Collapse>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={3}>
                                <Box className={classes.kpi}>
                                    <Typography className={classes.kpiLabel}>
                                        Messages sent
                                    </Typography>
                                    <Typography className={classes.kpiValue}>
                                        {stats.totalSent}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box className={classes.kpi}>
                                    <Typography className={classes.kpiLabel}>
                                        Total recipients
                                    </Typography>
                                    <Typography className={classes.kpiValue}>
                                        {stats.totalRecipients}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box className={classes.kpi}>
                                    <Typography className={classes.kpiLabel}>
                                        Total read
                                    </Typography>
                                    <Typography
                                        className={classes.kpiValue}
                                        style={{ color: '#10b981' }}
                                    >
                                        {stats.totalRead}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={3}>
                                <Box className={classes.kpi}>
                                    <Typography className={classes.kpiLabel}>
                                        Read rate
                                    </Typography>
                                    <Typography className={classes.kpiValue}>
                                        {stats.readRate}%
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Search + Filter Button */}
                        <Box className={classes.filterRow}>
                            <TextField
                                className={classes.search}
                                size="small"
                                variant="outlined"
                                placeholder="Search messages..."
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                fontSize="small"
                                                color="disabled"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterListIcon />}
                                onClick={() => setDrawerOpen(true)}
                                style={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor:
                                        activeFilterCount > 0
                                            ? '#008C85'
                                            : undefined,
                                    color:
                                        activeFilterCount > 0
                                            ? '#008C85'
                                            : undefined,
                                }}
                            >
                                Filters
                                {activeFilterCount > 0 && (
                                    <Chip
                                        label={activeFilterCount}
                                        size="small"
                                        style={{
                                            height: 18,
                                            fontSize: '0.625rem',
                                            fontWeight: 700,
                                            marginLeft: 6,
                                            backgroundColor: '#008C85',
                                            color: '#fff',
                                        }}
                                    />
                                )}
                            </Button>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginLeft: 'auto' }}
                            >
                                {totalCount} message
                                {totalCount !== 1 ? 's' : ''}
                            </Typography>
                        </Box>

                        {/* Active filter chips */}
                        {activeFilterCount > 0 && (
                            <Box className={classes.activeChips}>
                                {filters.initiated_by && (
                                    <Chip
                                        label={`Source: ${
                                            filters.initiated_by === 'tenant'
                                                ? 'Tenant'
                                                : 'Staff'
                                        }`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('initiated_by')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.unread_replies && (
                                    <Chip
                                        label="Unread replies"
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('unread_replies')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.has_replies !== undefined && (
                                    <Chip
                                        label={
                                            filters.has_replies
                                                ? 'Has replies'
                                                : 'No replies'
                                        }
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('has_replies')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.audience_type && (
                                    <Chip
                                        label={`Audience: ${
                                            filters.audience_type === 'ALL'
                                                ? 'All'
                                                : 'Selected'
                                        }`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('audience_type')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.individual && (
                                    <Chip
                                        label="1:1 Conversations"
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('individual')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.channel && (
                                    <Chip
                                        label={`Channel: ${
                                            filters.channel === 'portal_only'
                                                ? 'Portal Only'
                                                : filters.channel === 'email'
                                                ? 'Email'
                                                : 'SMS'
                                        }`}
                                        size="small"
                                        onDelete={() => removeFilter('channel')}
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.read_status && (
                                    <Chip
                                        label={`Read: ${
                                            filters.read_status === 'all_read'
                                                ? 'All Read'
                                                : filters.read_status ===
                                                  'partially_read'
                                                ? 'Partially Read'
                                                : 'Unread'
                                        }`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('read_status')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.created_by && (
                                    <Chip
                                        label={`By: ${
                                            userList.find(
                                                (u) =>
                                                    u.id === filters.created_by,
                                            )?.name || filters.created_by
                                        }`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('created_by')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.unit_ids &&
                                    filters.unit_ids.length > 0 && (
                                        <Chip
                                            label={`Unit${
                                                filters.unit_ids.length > 1
                                                    ? 's'
                                                    : ''
                                            }: ${filters.unit_ids
                                                .map(
                                                    (id) =>
                                                        unitNameMap[id] || id,
                                                )
                                                .join(', ')}`}
                                            size="small"
                                            onDelete={() => {
                                                setFilters((f) => {
                                                    const next = {
                                                        ...f,
                                                        page: 1,
                                                    }
                                                    delete next.unit_ids
                                                    delete next.area_ids
                                                    return next
                                                })
                                            }}
                                            style={{
                                                height: 24,
                                                fontSize: '0.6875rem',
                                            }}
                                        />
                                    )}
                                {filters.area_ids &&
                                    filters.area_ids.length > 0 && (
                                        <Chip
                                            label={`Area${
                                                filters.area_ids.length > 1
                                                    ? 's'
                                                    : ''
                                            }: ${
                                                filters.area_ids.length > 2
                                                    ? `${filters.area_ids.length} selected`
                                                    : filters.area_ids
                                                          .map(
                                                              (id) =>
                                                                  areaNameMap[
                                                                      id
                                                                  ] || id,
                                                          )
                                                          .join(', ')
                                            }`}
                                            size="small"
                                            onDelete={() =>
                                                removeFilter('area_ids')
                                            }
                                            style={{
                                                height: 24,
                                                fontSize: '0.6875rem',
                                            }}
                                        />
                                    )}
                                {filters.date_range && (
                                    <Chip
                                        label={`Date: ${filters.date_range.replace(
                                            /_/g,
                                            ' ',
                                        )}`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('date_range')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.created_after && (
                                    <Chip
                                        label={`After: ${formatDate(
                                            filters.created_after,
                                        )}`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('created_after')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                {filters.created_before && (
                                    <Chip
                                        label={`Before: ${formatDate(
                                            filters.created_before,
                                        )}`}
                                        size="small"
                                        onDelete={() =>
                                            removeFilter('created_before')
                                        }
                                        style={{
                                            height: 24,
                                            fontSize: '0.6875rem',
                                        }}
                                    />
                                )}
                                <Chip
                                    label="Clear all"
                                    size="small"
                                    variant="outlined"
                                    onClick={clearAllFilters}
                                    style={{
                                        height: 24,
                                        fontSize: '0.6875rem',
                                        fontWeight: 600,
                                    }}
                                />
                            </Box>
                        )}

                        {/* Table */}
                        <Box className={classes.tableCard}>
                            <TableContainer style={{ flex: 1 }}>
                                <Table size="small" stickyHeader>
                                    <TableHead className={classes.tableHead}>
                                        <TableRow>
                                            <TableCell
                                                padding="checkbox"
                                                style={{ width: 42 }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    color="primary"
                                                    indeterminate={
                                                        selectedIds.length >
                                                            0 &&
                                                        selectedIds.length <
                                                            messages.length
                                                    }
                                                    checked={
                                                        messages.length > 0 &&
                                                        selectedIds.length ===
                                                            messages.length
                                                    }
                                                    onChange={toggleSelectAll}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={
                                                        currentSort.column ===
                                                        'subject'
                                                    }
                                                    direction={
                                                        currentSort.column ===
                                                        'subject'
                                                            ? currentSort.direction
                                                            : 'asc'
                                                    }
                                                    onClick={() =>
                                                        handleSort('subject')
                                                    }
                                                >
                                                    Subject
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Audience</TableCell>
                                            <TableCell align="center">
                                                <TableSortLabel
                                                    active={
                                                        currentSort.column ===
                                                        'recipient_count'
                                                    }
                                                    direction={
                                                        currentSort.column ===
                                                        'recipient_count'
                                                            ? currentSort.direction
                                                            : 'desc'
                                                    }
                                                    onClick={() =>
                                                        handleSort(
                                                            'recipient_count',
                                                        )
                                                    }
                                                >
                                                    Recipients
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                style={{ minWidth: 160 }}
                                            >
                                                Read
                                            </TableCell>
                                            <TableCell>Channels</TableCell>
                                            <TableCell>Sent By</TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={
                                                        currentSort.column ===
                                                        'last_activity'
                                                    }
                                                    direction={
                                                        currentSort.column ===
                                                        'last_activity'
                                                            ? currentSort.direction
                                                            : 'desc'
                                                    }
                                                    onClick={() =>
                                                        handleSort(
                                                            'last_activity',
                                                        )
                                                    }
                                                >
                                                    Last Activity
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={
                                                        currentSort.column ===
                                                        'created_date'
                                                    }
                                                    direction={
                                                        currentSort.column ===
                                                        'created_date'
                                                            ? currentSort.direction
                                                            : 'desc'
                                                    }
                                                    onClick={() =>
                                                        handleSort(
                                                            'created_date',
                                                        )
                                                    }
                                                >
                                                    Created
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{ width: 80 }}
                                            />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {messages.length === 0 ? (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={11}
                                                    align="center"
                                                >
                                                    <Box py={4}>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                        >
                                                            {viewMode ===
                                                            'archived'
                                                                ? 'No archived messages.'
                                                                : 'No messages match your filters.'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            messages.map((msg) => {
                                                const pct =
                                                    msg.recipient_count > 0
                                                        ? Math.round(
                                                              (msg.read_count /
                                                                  msg.recipient_count) *
                                                                  100,
                                                          )
                                                        : 0

                                                return (
                                                    <TableRow
                                                        key={msg.id}
                                                        hover
                                                        style={{
                                                            cursor: 'pointer',
                                                            backgroundColor: selectedIds.includes(
                                                                msg.id,
                                                            )
                                                                ? 'rgba(0,140,133,0.04)'
                                                                : msg.has_unread_replies
                                                                ? '#f0fdf9'
                                                                : undefined,
                                                            borderLeft: msg.has_unread_replies
                                                                ? '3px solid #008C85'
                                                                : undefined,
                                                        }}
                                                        onClick={() =>
                                                            setSelectedMessage(
                                                                msg,
                                                            )
                                                        }
                                                    >
                                                        <TableCell
                                                            padding="checkbox"
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                        >
                                                            <Checkbox
                                                                size="small"
                                                                color="primary"
                                                                checked={selectedIds.includes(
                                                                    msg.id,
                                                                )}
                                                                onChange={() =>
                                                                    toggleSelect(
                                                                        msg.id,
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gridGap={4}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    style={{
                                                                        fontWeight: msg.has_unread_replies
                                                                            ? 700
                                                                            : 600,
                                                                    }}
                                                                >
                                                                    {
                                                                        msg.subject
                                                                    }
                                                                </Typography>
                                                                {msg.initiated_by_tenant && (
                                                                    <Chip
                                                                        label="Tenant"
                                                                        size="small"
                                                                        style={{
                                                                            height: 18,
                                                                            fontSize:
                                                                                '0.5625rem',
                                                                            fontWeight: 600,
                                                                            backgroundColor:
                                                                                '#ede9fe',
                                                                            color:
                                                                                '#7c3aed',
                                                                        }}
                                                                    />
                                                                )}
                                                                {msg.attachments &&
                                                                    msg
                                                                        .attachments
                                                                        .length >
                                                                        0 && (
                                                                        <Tooltip
                                                                            title={`${
                                                                                msg
                                                                                    .attachments
                                                                                    .length
                                                                            } attachment${
                                                                                msg
                                                                                    .attachments
                                                                                    .length !==
                                                                                1
                                                                                    ? 's'
                                                                                    : ''
                                                                            }`}
                                                                        >
                                                                            <AttachFileIcon
                                                                                style={{
                                                                                    fontSize: 14,
                                                                                    color:
                                                                                        '#6b7280',
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    )}
                                                                {msg.reply_count >
                                                                    0 && (
                                                                    <Tooltip
                                                                        title={`${
                                                                            msg.reply_count
                                                                        } repl${
                                                                            msg.reply_count ===
                                                                            1
                                                                                ? 'y'
                                                                                : 'ies'
                                                                        }${
                                                                            msg.has_unread_replies
                                                                                ? ' (unread)'
                                                                                : ''
                                                                        }`}
                                                                    >
                                                                        <Box
                                                                            display="flex"
                                                                            alignItems="center"
                                                                            gridGap={
                                                                                2
                                                                            }
                                                                        >
                                                                            {msg.has_unread_replies && (
                                                                                <Box
                                                                                    style={{
                                                                                        width: 6,
                                                                                        height: 6,
                                                                                        borderRadius:
                                                                                            '50%',
                                                                                        backgroundColor:
                                                                                            '#008C85',
                                                                                        flexShrink: 0,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            <ChatBubbleOutlineIcon
                                                                                style={{
                                                                                    fontSize: 13,
                                                                                    color: msg.has_unread_replies
                                                                                        ? '#008C85'
                                                                                        : '#6b7280',
                                                                                }}
                                                                            />
                                                                            <Typography
                                                                                variant="caption"
                                                                                style={{
                                                                                    fontSize:
                                                                                        '0.625rem',
                                                                                    fontFamily:
                                                                                        'monospace',
                                                                                    color: msg.has_unread_replies
                                                                                        ? '#008C85'
                                                                                        : '#6b7280',
                                                                                    fontWeight: msg.has_unread_replies
                                                                                        ? 700
                                                                                        : undefined,
                                                                                }}
                                                                            >
                                                                                {
                                                                                    msg.reply_count
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            {msg.audience_type ===
                                                            'ALL' ? (
                                                                <Chip
                                                                    label="All"
                                                                    size="small"
                                                                    style={{
                                                                        backgroundColor:
                                                                            'rgba(0,140,133,0.1)',
                                                                        color:
                                                                            '#008C85',
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.625rem',
                                                                        height: 22,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label={`${msg.recipient_count} selected`}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    style={{
                                                                        fontWeight: 500,
                                                                        fontSize:
                                                                            '0.625rem',
                                                                        height: 22,
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    fontFamily:
                                                                        'monospace',
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {
                                                                    msg.recipient_count
                                                                }
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gridGap={8}
                                                            >
                                                                <Box
                                                                    flex={1}
                                                                    className={
                                                                        classes.readBar
                                                                    }
                                                                >
                                                                    <Box
                                                                        className={
                                                                            classes.readBarFill
                                                                        }
                                                                        style={{
                                                                            width: `${pct}%`,
                                                                        }}
                                                                    />
                                                                </Box>
                                                                <Typography
                                                                    variant="caption"
                                                                    style={{
                                                                        fontFamily:
                                                                            'monospace',
                                                                        fontWeight: 500,
                                                                        minWidth: 56,
                                                                        textAlign:
                                                                            'right',
                                                                    }}
                                                                >
                                                                    {pct}%
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                gridGap={4}
                                                            >
                                                                <Tooltip title="Portal">
                                                                    <InboxIcon
                                                                        style={{
                                                                            fontSize: 15,
                                                                            color:
                                                                                '#008C85',
                                                                        }}
                                                                    />
                                                                </Tooltip>
                                                                {msg.send_email && (
                                                                    <Tooltip
                                                                        title={
                                                                            msg.email_sent_count !=
                                                                            null
                                                                                ? `Email: ${msg.email_sent_count}/${msg.recipient_count} delivered`
                                                                                : 'Email enabled'
                                                                        }
                                                                    >
                                                                        <EmailIcon
                                                                            style={{
                                                                                fontSize: 15,
                                                                                color:
                                                                                    '#008C85',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                                {msg.send_sms && (
                                                                    <Tooltip
                                                                        title={
                                                                            msg.sms_sent_count !=
                                                                            null
                                                                                ? `SMS: ${msg.sms_sent_count}/${msg.recipient_count} delivered`
                                                                                : 'SMS enabled'
                                                                        }
                                                                    >
                                                                        <SmsIcon
                                                                            style={{
                                                                                fontSize: 15,
                                                                                color:
                                                                                    '#008C85',
                                                                            }}
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="caption">
                                                                {
                                                                    msg.created_by_name
                                                                }
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    msg.last_activity_at,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                style={{
                                                                    display:
                                                                        'block',
                                                                    fontSize:
                                                                        '0.625rem',
                                                                }}
                                                            >
                                                                {formatTime(
                                                                    msg.last_activity_at,
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    whiteSpace:
                                                                        'nowrap',
                                                                }}
                                                            >
                                                                {formatDate(
                                                                    msg.created_date,
                                                                )}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                style={{
                                                                    display:
                                                                        'block',
                                                                    fontSize:
                                                                        '0.625rem',
                                                                }}
                                                            >
                                                                {formatTime(
                                                                    msg.created_date,
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                style={{
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                <Tooltip title="View details">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(
                                                                            e,
                                                                        ) => {
                                                                            e.stopPropagation()
                                                                            setSelectedMessage(
                                                                                msg,
                                                                            )
                                                                        }}
                                                                    >
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {viewMode ===
                                                                'active' ? (
                                                                    <Tooltip title="Archive">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation()
                                                                                handleArchive(
                                                                                    msg.id,
                                                                                )
                                                                            }}
                                                                        >
                                                                            <ArchiveIcon
                                                                                fontSize="small"
                                                                                style={{
                                                                                    color:
                                                                                        '#6b7280',
                                                                                }}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Tooltip title="Unarchive">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(
                                                                                e,
                                                                            ) => {
                                                                                e.stopPropagation()
                                                                                handleUnarchive(
                                                                                    msg.id,
                                                                                )
                                                                            }}
                                                                        >
                                                                            <UnarchiveIcon
                                                                                fontSize="small"
                                                                                style={{
                                                                                    color:
                                                                                        '#6b7280',
                                                                                }}
                                                                            />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    style={{
                                        padding: '10px 16px',
                                        borderTop: '1px solid #e6e9ed',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Showing{' '}
                                        {(currentPage - 1) * pageSize + 1}–
                                        {Math.min(
                                            currentPage * pageSize,
                                            totalCount,
                                        )}{' '}
                                        of {totalCount}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 8 }}
                                    >
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={!hasPreviousPage}
                                            onClick={() =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    page: currentPage - 1,
                                                }))
                                            }
                                            style={{
                                                textTransform: 'none',
                                                minWidth: 80,
                                            }}
                                        >
                                            Previous
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            style={{ fontWeight: 500 }}
                                        >
                                            Page {currentPage} of {totalPages}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            disabled={!hasNextPage}
                                            onClick={() =>
                                                setFilters((f) => ({
                                                    ...f,
                                                    page: currentPage + 1,
                                                }))
                                            }
                                            style={{
                                                textTransform: 'none',
                                                minWidth: 80,
                                            }}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </Box>

            {/* Filter Drawer */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
            >
                <Box
                    className={classes.drawer}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {/* Header */}
                    <Box
                        display="flex"
                        alignItems="center"
                        mb={2}
                        flexShrink={0}
                    >
                        <Box
                            display="flex"
                            alignItems="center"
                            style={{ gap: 10, flex: 1 }}
                        >
                            <Box
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 8,
                                    backgroundColor: 'rgba(0,140,133,0.08)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <FilterListIcon
                                    style={{ fontSize: 18, color: '#008C85' }}
                                />
                            </Box>
                            <Typography
                                variant="h6"
                                style={{ fontWeight: 700, fontSize: '1.1rem' }}
                            >
                                Filters
                            </Typography>
                            {activeFilterCount > 0 && (
                                <Chip
                                    label={activeFilterCount}
                                    size="small"
                                    style={{
                                        height: 20,
                                        fontSize: '0.6875rem',
                                        fontWeight: 700,
                                        backgroundColor: '#008C85',
                                        color: '#fff',
                                    }}
                                />
                            )}
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setDrawerOpen(false)}
                            style={{ backgroundColor: '#f3f4f6', padding: 6 }}
                        >
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>

                    <Tabs
                        value={drawerTab}
                        onChange={(_, val) => setDrawerTab(val)}
                        className={classes.drawerTabs}
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        <Tab label="Filters" />
                        <Tab label="Location" />
                    </Tabs>

                    {drawerTab === 0 && (
                        <Box
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                paddingBottom: 8,
                            }}
                        >
                            {/* Date Range */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                    style={{ marginBottom: 8 }}
                                >
                                    Date Range
                                </Typography>
                                <RadioGroup
                                    value={filters.date_range || ''}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setFilters((f) => ({
                                            ...f,
                                            date_range: val
                                                ? (val as MessageFilters['date_range'])
                                                : undefined,
                                            created_after: val
                                                ? undefined
                                                : f.created_after,
                                            created_before: val
                                                ? undefined
                                                : f.created_before,
                                            page: 1,
                                        }))
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All time"
                                    />
                                    <FormControlLabel
                                        value="today"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Today"
                                    />
                                    <FormControlLabel
                                        value="this_week"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="This week"
                                    />
                                    <FormControlLabel
                                        value="this_month"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="This month"
                                    />
                                    <FormControlLabel
                                        value="last_30_days"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Last 30 days"
                                    />
                                    <FormControlLabel
                                        value="last_90_days"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Last 90 days"
                                    />
                                </RadioGroup>

                                {!filters.date_range && (
                                    <Box
                                        mt={1.5}
                                        style={{ display: 'flex', gap: 8 }}
                                    >
                                        <TextField
                                            label="After"
                                            type="date"
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            value={
                                                filters.created_after?.split(
                                                    'T',
                                                )[0] || ''
                                            }
                                            onChange={(e) => {
                                                const val = e.target.value
                                                setFilters((f) => ({
                                                    ...f,
                                                    created_after: val
                                                        ? `${val}T00:00:00Z`
                                                        : undefined,
                                                    page: 1,
                                                }))
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            label="Before"
                                            type="date"
                                            size="small"
                                            variant="outlined"
                                            fullWidth
                                            value={
                                                filters.created_before?.split(
                                                    'T',
                                                )[0] || ''
                                            }
                                            onChange={(e) => {
                                                const val = e.target.value
                                                setFilters((f) => ({
                                                    ...f,
                                                    created_before: val
                                                        ? `${val}T23:59:59Z`
                                                        : undefined,
                                                    page: 1,
                                                }))
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                )}
                            </Box>

                            {/* Sent By */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                    style={{ marginBottom: 8 }}
                                >
                                    Sent By
                                </Typography>
                                <Selector
                                    data={userList}
                                    label="Select staff member"
                                    currentValue={filters.created_by || ''}
                                    onChange={(e) => {
                                        const val = e.target.value as
                                            | number
                                            | ''
                                        setFilters((f) => ({
                                            ...f,
                                            created_by: val
                                                ? (val as number)
                                                : undefined,
                                            page: 1,
                                        }))
                                    }}
                                    getDisplayString={(u) => u.name}
                                    searchable
                                    variant="outlined"
                                    size="small"
                                    noSelectionLabel="Anyone"
                                    customStyle={{
                                        formControl: { width: '100%' },
                                    }}
                                />
                            </Box>

                            {/* Thread Source */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                >
                                    Thread Source
                                </Typography>
                                <RadioGroup
                                    value={filters.initiated_by || ''}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setFilters((f) => ({
                                            ...f,
                                            initiated_by: val
                                                ? (val as 'staff' | 'tenant')
                                                : undefined,
                                            page: 1,
                                        }))
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All"
                                    />
                                    <FormControlLabel
                                        value="staff"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Staff sent"
                                    />
                                    <FormControlLabel
                                        value="tenant"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Tenant started"
                                    />
                                </RadioGroup>
                            </Box>

                            {/* Reply Status */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                >
                                    Reply Status
                                </Typography>
                                <RadioGroup
                                    value={
                                        filters.unread_replies
                                            ? 'unread'
                                            : filters.has_replies === true
                                            ? 'has_replies'
                                            : filters.has_replies === false
                                            ? 'no_replies'
                                            : ''
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setFilters((f) => {
                                            const next = { ...f, page: 1 }
                                            delete next.has_replies
                                            delete next.unread_replies
                                            if (val === 'unread')
                                                next.unread_replies = true
                                            else if (val === 'has_replies')
                                                next.has_replies = true
                                            else if (val === 'no_replies')
                                                next.has_replies = false
                                            return next
                                        })
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All"
                                    />
                                    <FormControlLabel
                                        value="unread"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Unread replies"
                                    />
                                    <FormControlLabel
                                        value="has_replies"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Has replies"
                                    />
                                    <FormControlLabel
                                        value="no_replies"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="No replies"
                                    />
                                </RadioGroup>
                            </Box>

                            {/* Audience Type */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                >
                                    Audience
                                </Typography>
                                <RadioGroup
                                    value={
                                        filters.individual
                                            ? 'individual'
                                            : filters.audience_type || ''
                                    }
                                    onChange={(e) => {
                                        const val = e.target.value
                                        if (val === 'individual') {
                                            setFilters((f) => ({
                                                ...f,
                                                individual: true,
                                                audience_type: undefined,
                                                page: 1,
                                            }))
                                        } else {
                                            setFilters((f) => ({
                                                ...f,
                                                audience_type: val
                                                    ? (val as
                                                          | 'ALL'
                                                          | 'SELECTED')
                                                    : undefined,
                                                individual: undefined,
                                                page: 1,
                                            }))
                                        }
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All"
                                    />
                                    <FormControlLabel
                                        value="ALL"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Sent to all tenants"
                                    />
                                    <FormControlLabel
                                        value="SELECTED"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Selected tenants"
                                    />
                                    <FormControlLabel
                                        value="individual"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="1:1 Conversations"
                                    />
                                </RadioGroup>
                            </Box>

                            {/* Channel */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                >
                                    Delivery Channel
                                </Typography>
                                <RadioGroup
                                    value={filters.channel || ''}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setFilters((f) => ({
                                            ...f,
                                            channel: val
                                                ? (val as
                                                      | 'email'
                                                      | 'sms'
                                                      | 'portal_only')
                                                : undefined,
                                            page: 1,
                                        }))
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All channels"
                                    />
                                    <FormControlLabel
                                        value="email"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Email"
                                    />
                                    <FormControlLabel
                                        value="sms"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="SMS"
                                    />
                                    <FormControlLabel
                                        value="portal_only"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Portal only"
                                    />
                                </RadioGroup>
                            </Box>

                            {/* Read Status */}
                            <Box className={classes.drawerSection}>
                                <Typography
                                    className={classes.drawerSectionLabel}
                                >
                                    Read Status
                                </Typography>
                                <RadioGroup
                                    value={filters.read_status || ''}
                                    onChange={(e) => {
                                        const val = e.target.value
                                        setFilters((f) => ({
                                            ...f,
                                            read_status: val
                                                ? (val as
                                                      | 'all_read'
                                                      | 'partially_read'
                                                      | 'unread')
                                                : undefined,
                                            page: 1,
                                        }))
                                    }}
                                >
                                    <FormControlLabel
                                        value=""
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Any"
                                    />
                                    <FormControlLabel
                                        value="all_read"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="All read"
                                    />
                                    <FormControlLabel
                                        value="partially_read"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Partially read"
                                    />
                                    <FormControlLabel
                                        value="unread"
                                        control={
                                            <Radio
                                                size="small"
                                                color="primary"
                                            />
                                        }
                                        label="Unread"
                                    />
                                </RadioGroup>
                            </Box>
                        </Box>
                    )}

                    {drawerTab === 1 && (
                        <Box
                            style={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <TextField
                                className={classes.treeSearch}
                                size="small"
                                variant="outlined"
                                placeholder="Search units or areas..."
                                fullWidth
                                value={treeSearch}
                                onChange={(e) => setTreeSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon
                                                fontSize="small"
                                                color="disabled"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {((filters.unit_ids &&
                                filters.unit_ids.length > 0) ||
                                (filters.area_ids &&
                                    filters.area_ids.length > 0)) && (
                                <Box
                                    mb={1.5}
                                    display="flex"
                                    alignItems="flex-start"
                                    flexWrap="wrap"
                                    style={{
                                        gap: 6,
                                        padding: '10px 12px',
                                        borderRadius: 8,
                                        backgroundColor: '#f0fdf9',
                                        border:
                                            '1px solid rgba(0,140,133,0.15)',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        style={{
                                            fontWeight: 600,
                                            color: '#008C85',
                                            width: '100%',
                                            marginBottom: 4,
                                            fontSize: '0.6875rem',
                                        }}
                                    >
                                        Selected locations
                                    </Typography>
                                    {filters.unit_ids?.map((id) => (
                                        <Chip
                                            key={`unit-chip-${id}`}
                                            label={
                                                unitNameMap[id] || `Unit ${id}`
                                            }
                                            size="small"
                                            onDelete={() =>
                                                setFilters((f) => {
                                                    const newUnitIds = (
                                                        f.unit_ids || []
                                                    ).filter(
                                                        (uid) => uid !== id,
                                                    )
                                                    const next = {
                                                        ...f,
                                                        page: 1,
                                                    }
                                                    if (
                                                        newUnitIds.length === 0
                                                    ) {
                                                        delete next.unit_ids
                                                        delete next.area_ids
                                                    } else {
                                                        next.unit_ids = newUnitIds
                                                    }
                                                    return next
                                                })
                                            }
                                            style={{
                                                height: 24,
                                                fontSize: '0.6875rem',
                                                fontWeight: 500,
                                                backgroundColor: '#fff',
                                                border:
                                                    '1px solid rgba(0,140,133,0.3)',
                                                color: '#008C85',
                                            }}
                                        />
                                    ))}
                                    {filters.area_ids?.map((id) => (
                                        <Chip
                                            key={`area-chip-${id}`}
                                            label={
                                                areaNameMap[id] || `Area ${id}`
                                            }
                                            size="small"
                                            onDelete={() =>
                                                setFilters((f) => {
                                                    const newAreaIds = (
                                                        f.area_ids || []
                                                    ).filter(
                                                        (aid) => aid !== id,
                                                    )
                                                    return {
                                                        ...f,
                                                        area_ids:
                                                            newAreaIds.length >
                                                            0
                                                                ? newAreaIds
                                                                : undefined,
                                                        page: 1,
                                                    }
                                                })
                                            }
                                            style={{
                                                height: 24,
                                                fontSize: '0.6875rem',
                                                fontWeight: 500,
                                                backgroundColor: '#fff',
                                                border:
                                                    '1px solid rgba(124,58,237,0.3)',
                                                color: '#7c3aed',
                                            }}
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box className={classes.treeContainer}>
                                <LocationTree
                                    tree={tree?.root}
                                    occupiableAreaConfigIds={
                                        occupiableAreaConfigIds
                                    }
                                    selectedUnitIds={filters.unit_ids || []}
                                    selectedAreaIds={filters.area_ids || []}
                                    searchQuery={treeSearch}
                                    openState={treeOpenState}
                                    setOpenState={setTreeOpenState}
                                    onSelectUnit={(unitId) => {
                                        setFilters((f) => {
                                            const current = f.unit_ids || []
                                            const isSelected = current.includes(
                                                unitId,
                                            )
                                            const newUnitIds = isSelected
                                                ? current.filter(
                                                      (id) => id !== unitId,
                                                  )
                                                : [...current, unitId]
                                            const next = { ...f, page: 1 }
                                            if (newUnitIds.length > 0) {
                                                next.unit_ids = newUnitIds
                                            } else {
                                                delete next.unit_ids
                                                delete next.area_ids
                                            }
                                            return next
                                        })
                                    }}
                                    onSelectArea={(areaId) => {
                                        setFilters((f) => {
                                            const current = f.area_ids || []
                                            const isSelected = current.includes(
                                                areaId,
                                            )
                                            const newAreaIds = isSelected
                                                ? current.filter(
                                                      (id) => id !== areaId,
                                                  )
                                                : [...current, areaId]
                                            return {
                                                ...f,
                                                area_ids:
                                                    newAreaIds.length > 0
                                                        ? newAreaIds
                                                        : undefined,
                                                page: 1,
                                            }
                                        })
                                    }}
                                    onSelectFolder={(unitIds) => {
                                        setFilters((f) => {
                                            const current = f.unit_ids || []
                                            const allAlreadySelected = unitIds.every(
                                                (id) => current.includes(id),
                                            )
                                            let newUnitIds: number[]
                                            if (allAlreadySelected) {
                                                newUnitIds = current.filter(
                                                    (id) =>
                                                        !unitIds.includes(id),
                                                )
                                            } else {
                                                const toAdd = unitIds.filter(
                                                    (id) =>
                                                        !current.includes(id),
                                                )
                                                newUnitIds = [
                                                    ...current,
                                                    ...toAdd,
                                                ]
                                            }
                                            const next = { ...f, page: 1 }
                                            if (newUnitIds.length > 0) {
                                                next.unit_ids = newUnitIds
                                            } else {
                                                delete next.unit_ids
                                                delete next.area_ids
                                            }
                                            return next
                                        })
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Actions */}
                    <Box
                        display="flex"
                        style={{
                            gap: 10,
                            paddingTop: 16,
                            borderTop: '1px solid #e6e9ed',
                        }}
                        mt={2}
                        flexShrink={0}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            onClick={clearAllFilters}
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 8,
                            }}
                        >
                            Clear All
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            fullWidth
                            onClick={() => setDrawerOpen(false)}
                            style={{
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 8,
                                boxShadow: 'none',
                            }}
                        >
                            Done
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <ComposeMessageModal
                open={composeOpen}
                handleClose={() => setComposeOpen(false)}
            />

            <RecipientsModal
                open={!!selectedMessage}
                message={selectedMessage}
                handleClose={() => setSelectedMessage(null)}
            />

            <Dialog
                open={!!archiveConfirm}
                onClose={() => setArchiveConfirm(null)}
                PaperProps={{
                    style: { borderRadius: 12, minWidth: 380 },
                }}
            >
                <DialogTitle style={{ paddingBottom: 8 }}>
                    <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                        <Box
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 10,
                                backgroundColor: 'rgba(245,158,11,0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <ArchiveIcon
                                style={{ fontSize: 20, color: '#f59e0b' }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            style={{ fontWeight: 700, fontSize: '1.1rem' }}
                        >
                            Archive{' '}
                            {archiveConfirm?.type === 'bulk'
                                ? `${archiveConfirm.ids.length} threads`
                                : 'thread'}
                            ?
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        style={{ color: '#374151', fontSize: '0.875rem' }}
                    >
                        {archiveConfirm?.type === 'bulk'
                            ? `Are you sure you want to archive ${
                                  archiveConfirm.ids.length
                              } selected thread${
                                  archiveConfirm.ids.length !== 1 ? 's' : ''
                              }? They will be hidden from the active view but can be restored from the Archived tab.`
                            : 'This thread will be hidden from the active view. You can restore it anytime from the Archived tab.'}
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ padding: '8px 24px 20px' }}>
                    <Button
                        onClick={() => setArchiveConfirm(null)}
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 8,
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={confirmArchive}
                        style={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 8,
                            backgroundColor: '#f59e0b',
                            color: '#fff',
                            boxShadow: 'none',
                        }}
                    >
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
